import React from 'react'
import { FileData } from '@/types'
import { Button } from '@/components/ui/Button'
import { Download, Trash, ArrowCounterClockwise } from 'phosphor-react'
import { downloadCertificate, getCertificateContent } from '@/utils/certificate-utils'

interface CertificateDisplayProps {
  certificate: FileData
  title?: string
  showContent?: boolean
  onRemove?: () => void
  showRemove?: boolean
  isRemoved?: boolean
  onRestore?: () => void
}

export const CertificateDisplay: React.FC<CertificateDisplayProps> = ({
  certificate,
  title,
  showContent = true,
  onRemove,
  showRemove = false,
  isRemoved = false,
  onRestore,
}) => {
  const handleDownload = () => {
    downloadCertificate(certificate)
  }

  return (
    <div
      className={`p-3 border rounded-lg transition-all ${
        isRemoved
          ? 'bg-red-50 border-red-300'
          : 'bg-white border-[var(--color-border)]'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium ${
              isRemoved
                ? 'text-red-700 line-through'
                : 'text-[var(--color-text-primary)]'
            }`}
          >
            {title || certificate.filename}
          </span>
          {isRemoved && (
            <span className="text-xs text-red-600 font-medium">(Supprimé)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRemoved && onRestore && (
            <Button onClick={onRestore} variant="outline" size="sm" title="Conserver">
              <ArrowCounterClockwise className="w-4 h-4 mr-1" weight="regular" />
              Conserver
            </Button>
          )}
          {showRemove && onRemove && !isRemoved && (
            <Button onClick={onRemove} variant="danger" size="sm" title="Supprimer">
              <Trash className="w-4 h-4" weight="regular" />
            </Button>
          )}
          {!isRemoved && <Button
            onClick={handleDownload}
            variant="outline"
            size="sm"
            disabled={isRemoved}
          >
            <Download className="w-4 h-4 mr-2" weight="regular" />
            Télécharger
          </Button>}
        </div>
      </div>
      {showContent && (
        <div className="mt-2">
          <span className="text-xs text-[var(--color-text-secondary)] block mb-1">
            Contenu:
          </span>
          <div
            className={`p-2 border rounded text-xs font-mono break-all max-h-32 overflow-y-auto ${
              isRemoved
                ? 'bg-red-100 border-red-200 text-red-800'
                : 'bg-[var(--color-gray-50)] border-[var(--color-border)] text-[var(--color-text-primary)]'
            }`}
          >
            {getCertificateContent(certificate)}
          </div>
        </div>
      )}
    </div>
  )
}

interface CertificateListDisplayProps {
  certificates: FileData[]
  title: string
  showContent?: boolean
  onRemove?: (index: number) => void
  showRemove?: boolean
  removedIndices?: Set<number>
  onRestore?: (index: number) => void
}

export const CertificateListDisplay: React.FC<CertificateListDisplayProps> = ({
  certificates,
  title,
  showContent = true,
  onRemove,
  showRemove = false,
  removedIndices = new Set(),
  onRestore,
}) => {
  if (certificates.length === 0) {
    return null
  }

  const activeCount = certificates.length - removedIndices.size

  return (
    <div className="p-4 bg-[var(--color-gray-50)] border border-[var(--color-border)] rounded-lg">
      <h4 className="text-sm font-medium text-[var(--color-text-primary)] mb-3">
        {title} ({activeCount}{removedIndices.size > 0 ? `, ${removedIndices.size} supprimé${removedIndices.size > 1 ? 's' : ''}` : ''})
      </h4>
      <div className="space-y-4">
        {certificates.map((cert, index) => (
          <CertificateDisplay
            key={index}
            certificate={cert}
            showContent={showContent}
            onRemove={onRemove ? () => onRemove(index) : undefined}
            showRemove={showRemove}
            isRemoved={removedIndices.has(index)}
            onRestore={onRestore ? () => onRestore(index) : undefined}
          />
        ))}
      </div>
    </div>
  )
}

